import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import { api, API } from "../lib/api";
import { Radio, Users, Link as LinkIcon, Copy, CheckCircle2 } from "lucide-react";

/**
 * Live Observation Room — stakeholders watch incoming interview snippets stream in
 * in real-time via the /api/campaigns/{id}/observe SSE channel.
 *
 * Auth modes:
 *   - Owning client (logged in) → no token needed.
 *   - Stakeholder with a signed observation token in ?token=… → no login required.
 */
export default function LiveObservation() {
  const { campaignId } = useParams();
  const [search] = useSearchParams();
  const token = search.get("token");

  const [snapshot, setSnapshot] = useState({ current_completes: 0, target: 0, by_source: {}, title: "" });
  const [feed, setFeed] = useState([]); // [{event, snippet, source, ts}]
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);

  // Owner-only: mint a shareable observation link
  const [shareToken, setShareToken] = useState(null);
  const [copied, setCopied] = useState(false);

  const esRef = useRef(null);

  useEffect(() => {
    const url = `${API}/campaigns/${campaignId}/observe${token ? `?token=${encodeURIComponent(token)}` : ""}`;
    const es = new EventSource(url, { withCredentials: !token });
    esRef.current = es;
    es.addEventListener("snapshot", (e) => {
      try { setSnapshot(JSON.parse(e.data)); setConnected(true); } catch { /* malformed snapshot ignored */ }
    });
    es.addEventListener("transcript", (e) => {
      try {
        const evt = JSON.parse(e.data);
        setFeed((f) => [{ ...evt, _id: `${evt.ts}-${Math.random()}` }, ...f].slice(0, 50));
      } catch { /* malformed transcript event ignored */ }
    });
    es.addEventListener("complete", (e) => {
      try {
        const evt = JSON.parse(e.data);
        setSnapshot((s) => ({ ...s, current_completes: (s.current_completes || 0) + 1, by_source: { ...s.by_source, [evt.source]: (s.by_source?.[evt.source] || 0) + 1 } }));
      } catch { /* malformed complete event ignored */ }
    });
    es.onerror = () => { setError("Connection lost — retrying…"); setConnected(false); };
    return () => { es.close(); };
  }, [campaignId, token]);

  const mintShareLink = async () => {
    try {
      const { data } = await api.post(`/campaigns/${campaignId}/observe-token`);
      setShareToken(data.token);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const shareUrl = shareToken
    ? `${window.location.origin}/observe/${campaignId}?token=${shareToken}`
    : null;

  const copyShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard write blocked; user can long-press to copy */ }
  };

  const pct = snapshot.target > 0 ? Math.min(100, Math.round((snapshot.current_completes / snapshot.target) * 100)) : 0;

  return (
    <div className="min-h-screen bg-[var(--rl-bg-subtle)]" data-testid="live-observation-page">
      {!token && <Navigation />}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-3">
          <Radio className={`w-4 h-4 ${connected ? "text-[var(--rl-success)]" : "text-[var(--rl-text-secondary)]"} ${connected ? "animate-pulse" : ""}`} />
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Live observation room</div>
        </div>
        <h1 className="font-display font-black text-4xl tracking-tight" data-testid="live-title">{snapshot.title || "Live Pulse"}</h1>

        {/* Progress strip */}
        <div className="mt-8 bg-white border border-[var(--rl-border)] p-6">
          <div className="flex items-baseline justify-between">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Completes</div>
            <div className="font-display font-black text-3xl tabular-nums" data-testid="live-completes">{snapshot.current_completes} <span className="text-[var(--rl-text-secondary)] text-base font-mono">/ {snapshot.target}</span></div>
          </div>
          <div className="mt-3 h-1.5 bg-[var(--rl-bg-subtle)]">
            <div className="h-full bg-[var(--rl-accent)] transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-4 grid grid-cols-3 gap-4 text-xs">
            {[
              ["Internal panel", snapshot.by_source?.internal || 0],
              ["Programmatic", snapshot.by_source?.cloudresearch || 0],
              ["Intercept", snapshot.by_source?.intercept || 0],
            ].map(([label, count]) => (
              <div key={label} className="border-l-2 border-[var(--rl-border)] pl-3">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rl-text-secondary)]">{label}</div>
                <div className="font-mono font-bold text-lg mt-0.5">{count}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Stakeholder share — owner only */}
        {!token && (
          <div className="mt-6 bg-white border border-[var(--rl-border)] p-5" data-testid="share-block">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Share with stakeholders</div>
                <div className="text-sm mt-1">Mint a 24h signed URL anyone can open — no login required.</div>
              </div>
              {!shareToken ? (
                <button onClick={mintShareLink} className="inline-flex items-center gap-2 bg-[var(--rl-text)] text-white px-4 py-2 font-semibold text-sm hover:bg-[var(--rl-brand)] transition-colors" data-testid="mint-observe-btn">
                  <LinkIcon className="w-4 h-4" /> Generate link
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <input readOnly value={shareUrl} className="w-80 bg-[var(--rl-bg-subtle)] border border-[var(--rl-border)] px-3 py-2 text-xs font-mono" data-testid="share-url-input" />
                  <button onClick={copyShare} className="inline-flex items-center gap-2 bg-[var(--rl-brand)] text-white px-3 py-2 font-semibold text-sm hover:bg-[var(--rl-text)] transition-colors" data-testid="copy-share-btn">
                    {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Live transcript feed */}
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-[var(--rl-brand)]" />
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Anonymized panelist responses (streaming)</div>
          </div>
          <div className="space-y-2" data-testid="live-feed">
            {feed.length === 0 ? (
              <div className="bg-white border border-dashed border-[var(--rl-border)] p-8 text-center text-sm text-[var(--rl-text-secondary)]">
                Waiting for the next response…
              </div>
            ) : (
              feed.map((evt) => (
                <div key={evt._id} className="bg-white border border-[var(--rl-border)] p-4 text-sm" data-testid="live-feed-item">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rl-text-secondary)]">{evt.source}</span>
                    <span className="text-[10px] text-[var(--rl-text-secondary)]">·</span>
                    <span className="text-[10px] text-[var(--rl-text-secondary)]">{new Date(evt.ts).toLocaleTimeString()}</span>
                  </div>
                  <div className="leading-relaxed">{evt.snippet}</div>
                </div>
              ))
            )}
          </div>
        </div>

        {error && <div className="mt-4 text-sm text-[var(--rl-accent)]" data-testid="live-error">{error}</div>}

        {!token && (
          <div className="mt-12">
            <Link to={`/client/campaigns/${campaignId}`} className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)] hover:text-[var(--rl-text)]">← Back to campaign</Link>
          </div>
        )}
      </div>
    </div>
  );
}
